import { useUnit } from 'effector-react';
import {
  ArrowLeft,
  ArrowRight,
  BookOpen,
  Check,
  CircleHelp,
  Compass,
  FileText,
  GitCompareArrows,
  Keyboard,
  LockKeyhole,
  ScanSearch,
  Sparkles,
  Waves,
  CalendarRange,
  X,
} from 'lucide-react';
import { Dialog as RadixDialog, Tabs as RadixTabs } from 'radix-ui';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { $project, $readOnly } from '../../entities/project';
import { cn } from '../../shared/lib/cn';
import { useHotkeys } from '../../shared/lib/hotkeys';
import {
  SHORTCUTS,
  shortcutOf,
  type ShortcutDef,
  type ShortcutId,
} from '../../shared/lib/shortcuts';
import { COMPACT_QUERY, useMediaQuery } from '../../shared/lib/use-media-query';
import {
  Button,
  IconButton,
  Menu,
  MenuContent,
  MenuItem,
  MenuSeparator,
  MenuTrigger,
  Shortcut,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Tooltip,
} from '../../shared/ui';
import { HeroIllustration, TutorialIllustration } from './illustrations';
import {
  $helpTab,
  $tutorialStep,
  helpClosed,
  helpOpened,
  helpTabChanged,
  HELP_TABS,
  tourRequested,
  tutorialStepChanged,
  TUTORIAL_STEPS,
  type HelpTab,
  type TutorialStep,
} from './model';

/** The tour needs a project on screen at desktop width (its coach marks point at the tree). */
function useCanTour(): boolean {
  const [project, readOnly] = useUnit([$project, $readOnly]);
  const compact = useMediaQuery(COMPACT_QUERY);
  return project !== null && !readOnly && !compact;
}

/** Key caps for a registry shortcut, with "then"/"or" between combos. */
export function ShortcutKeys({ def }: { def: ShortcutDef }) {
  const { t } = useTranslation();
  return (
    <span className="flex items-center gap-1 text-2xs text-text-3">
      {def.combos.map((combo, i) => (
        <span key={combo.join('+')} className="flex items-center gap-1">
          {i > 0 && <span>{def.sequence ? t('shortcuts.then') : t('shortcuts.or')}</span>}
          <Shortcut keys={combo} />
        </span>
      ))}
    </span>
  );
}

// ── Help menu ────────────────────────────────────────────────────────────────

/** The top bar's Help menu: the help center's tabs, and the tour when it can run. */
export function HelpMenu() {
  const { t } = useTranslation();
  const [open, startTour] = useUnit([helpOpened, tourRequested]);
  const canTour = useCanTour();
  return (
    <Menu>
      <Tooltip content={t('help.menu')}>
        <MenuTrigger asChild>
          <IconButton label={t('help.menu')}>
            <CircleHelp />
          </IconButton>
        </MenuTrigger>
      </Tooltip>
      <MenuContent className="min-w-56">
        <MenuItem icon={<Sparkles />} onSelect={() => open('welcome')}>
          {t('help.menuItems.welcome')}
        </MenuItem>
        <MenuItem icon={<BookOpen />} onSelect={() => open('tutorial')}>
          {t('help.menuItems.tutorial')}
        </MenuItem>
        <MenuItem icon={<Keyboard />} shortcut={['?']} onSelect={() => open('shortcuts')}>
          {t('help.menuItems.shortcuts')}
        </MenuItem>
        {canTour && (
          <>
            <MenuSeparator />
            <MenuItem icon={<Compass />} onSelect={startTour}>
              {t('help.menuItems.tour')}
            </MenuItem>
          </>
        )}
      </MenuContent>
    </Menu>
  );
}

// ── The help center ──────────────────────────────────────────────────────────

const TAB_ICON: Record<HelpTab, ReactNode> = {
  welcome: <Sparkles aria-hidden />,
  tutorial: <BookOpen aria-hidden />,
  shortcuts: <Keyboard aria-hidden />,
};

const isHelpTab = (value: string): value is HelpTab =>
  (HELP_TABS as readonly string[]).includes(value);

/** The help center dialog (mounted once in the app); `?` opens it on the shortcuts. */
export function HelpCenter() {
  const { t } = useTranslation();
  const [tab, open, close, changeTab] = useUnit([$helpTab, helpOpened, helpClosed, helpTabChanged]);
  useHotkeys({ '?': () => open('shortcuts') });
  return (
    <RadixDialog.Root
      open={tab !== null}
      onOpenChange={(next) => {
        if (!next) close();
      }}
    >
      <RadixDialog.Portal>
        <RadixDialog.Overlay className="fixed inset-0 z-40 animate-fade-in bg-overlay" />
        <RadixDialog.Content
          aria-describedby={undefined}
          className={cn(
            'fixed top-1/2 left-1/2 z-50 flex h-[min(680px,calc(100dvh-32px))] w-[min(960px,calc(100vw-24px))]',
            '-translate-x-1/2 -translate-y-1/2 animate-scale-in flex-col overflow-hidden',
            'rounded-xl bg-bg-2 text-text-1 shadow-popover outline-none',
          )}
        >
          <Tabs
            value={tab ?? 'welcome'}
            onValueChange={(value) => {
              if (isHelpTab(value)) changeTab(value);
            }}
            className="flex min-h-0 flex-1 flex-col"
          >
            <header className="flex flex-wrap items-center gap-x-4 gap-y-2 border-b border-border-subtle px-4 py-2.5">
              <div className="flex items-center gap-2">
                <img src="/favicon.svg" alt="" className="size-5" />
                <RadixDialog.Title className="text-sm font-medium">
                  {t('help.title')}
                </RadixDialog.Title>
              </div>
              <TabsList
                aria-label={t('help.title')}
                className="order-last w-full sm:order-none sm:w-auto"
              >
                {HELP_TABS.map((value) => (
                  <TabsTrigger
                    key={value}
                    value={value}
                    className="flex items-center gap-1.5 [&_svg]:size-3.5"
                  >
                    {TAB_ICON[value]}
                    {t(`help.tabs.${value}`)}
                  </TabsTrigger>
                ))}
              </TabsList>
              <div className="flex-1" />
              <RadixDialog.Close asChild>
                <IconButton label={t('common.close')} size="sm">
                  <X />
                </IconButton>
              </RadixDialog.Close>
            </header>
            <TabsContent value="welcome" className="min-h-0 flex-1 overflow-y-auto">
              <Welcome />
            </TabsContent>
            <TabsContent value="tutorial" className="min-h-0 flex-1 overflow-y-auto">
              <Tutorial />
            </TabsContent>
            <TabsContent value="shortcuts" className="min-h-0 flex-1 overflow-y-auto">
              <Shortcuts />
            </TabsContent>
          </Tabs>
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
}

// ── Welcome ──────────────────────────────────────────────────────────────────

const FEATURES = [
  ['monthly', <CalendarRange key="i" />],
  ['compare', <GitCompareArrows key="i" />],
  ['uncertainty', <Waves key="i" />],
  ['explain', <ScanSearch key="i" />],
  ['private', <LockKeyhole key="i" />],
  ['share', <FileText key="i" />],
] as const;

const DIFFERENT = ['fair', 'real', 'portable', 'keyboard'] as const;

function Welcome() {
  const { t } = useTranslation();
  const [changeTab, close, startTour] = useUnit([helpTabChanged, helpClosed, tourRequested]);
  const canTour = useCanTour();
  const readTutorial = () => {
    changeTab('tutorial');
  };
  return (
    <div className="mx-auto max-w-[880px] px-5 py-6 sm:px-8 sm:py-8">
      <section className="grid items-center gap-6 md:grid-cols-[minmax(0,1.15fr)_minmax(0,1fr)] md:gap-10">
        <div>
          <p className="text-xs font-semibold tracking-wider text-accent uppercase">
            {t('help.welcome.eyebrow')}
          </p>
          <h2 className="mt-2 text-2xl leading-tight font-semibold tracking-tight text-balance sm:text-[28px]">
            {t('help.welcome.headline')}
          </h2>
          <p className="mt-3 text-sm leading-relaxed text-pretty text-text-2">
            {t('help.welcome.lead')}
          </p>
          <div className="mt-5 flex flex-wrap items-center gap-2">
            {canTour ? (
              <>
                <Button variant="primary" leadingIcon={<Compass />} onClick={startTour}>
                  {t('help.welcome.tour')}
                </Button>
                <Button leadingIcon={<BookOpen />} onClick={readTutorial}>
                  {t('help.welcome.tutorial')}
                </Button>
              </>
            ) : (
              <Button variant="primary" leadingIcon={<BookOpen />} onClick={readTutorial}>
                {t('help.welcome.tutorial')}
              </Button>
            )}
            <Button variant="ghost" onClick={close}>
              {t('help.welcome.explore')}
            </Button>
          </div>
          {canTour && <p className="mt-2 text-xs text-text-3">{t('help.welcome.tourHint')}</p>}
        </div>
        <div className="overflow-hidden rounded-xl border border-border-subtle bg-bg-1">
          <HeroIllustration />
        </div>
      </section>

      <section aria-labelledby="help-why" className="mt-10">
        <h3 id="help-why" className="text-sm font-semibold">
          {t('help.welcome.whyTitle')}
        </h3>
        <ul className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {FEATURES.map(([key, icon]) => (
            <li key={key} className="rounded-lg border border-border-subtle bg-bg-1 p-4">
              <span
                aria-hidden
                className="flex size-8 items-center justify-center rounded-md bg-accent-subtle text-accent [&_svg]:size-4"
              >
                {icon}
              </span>
              <h4 className="mt-3 text-sm font-medium">
                {t(`help.welcome.features.${key}.title`)}
              </h4>
              <p className="mt-1 text-xs leading-relaxed text-text-2">
                {t(`help.welcome.features.${key}.body`)}
              </p>
            </li>
          ))}
        </ul>
      </section>

      <section aria-labelledby="help-different" className="mt-8">
        <h3 id="help-different" className="text-sm font-semibold">
          {t('help.welcome.differentTitle')}
        </h3>
        <ul className="mt-3 grid gap-x-8 gap-y-3 rounded-lg bg-bg-1 p-4 sm:grid-cols-2">
          {DIFFERENT.map((key) => (
            <li key={key} className="flex gap-2.5 text-xs leading-relaxed text-text-2">
              <Check aria-hidden className="mt-0.5 size-3.5 shrink-0 text-accent" />
              <span>
                <span className="font-medium text-text-1">
                  {t(`help.welcome.different.${key}.lead`)}.
                </span>{' '}
                {t(`help.welcome.different.${key}.body`)}
              </span>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}

// ── Tutorial ─────────────────────────────────────────────────────────────────

/** A shortcut to try at each step. */
const TRY_IT: Record<TutorialStep, ShortcutId> = {
  projects: 'newVariant',
  items: 'createItem',
  assumptions: 'assumptions',
  charts: 'views',
  compare: 'monteCarlo',
  share: 'exportReport',
};

function Tutorial() {
  const { t } = useTranslation();
  const [step, setStep, close, startTour] = useUnit([
    $tutorialStep,
    tutorialStepChanged,
    helpClosed,
    tourRequested,
  ]);
  const canTour = useCanTour();
  const last = TUTORIAL_STEPS.length - 1;
  const total = TUTORIAL_STEPS.length;

  return (
    <RadixTabs.Root
      orientation="vertical"
      value={String(step)}
      onValueChange={(value) => {
        setStep(Number(value));
      }}
      className="grid h-full md:grid-cols-[232px_minmax(0,1fr)]"
    >
      <RadixTabs.List
        aria-label={t('help.tutorial.steps')}
        className="flex gap-1 overflow-x-auto border-b border-border-subtle p-3 md:flex-col md:overflow-visible md:border-r md:border-b-0"
      >
        {TUTORIAL_STEPS.map((id, i) => (
          <RadixTabs.Trigger
            key={id}
            value={String(i)}
            className={cn(
              'group flex shrink-0 items-center gap-2.5 rounded-md px-2 py-1.5 text-left text-sm text-text-2 outline-none',
              'hover:bg-bg-3 hover:text-text-1 focus-visible:ring-2 focus-visible:ring-focus',
              'aria-selected:bg-bg-4 aria-selected:text-text-1',
            )}
          >
            <span
              aria-hidden
              className={cn(
                'flex size-5 shrink-0 items-center justify-center rounded-full border text-2xs font-semibold tabular-nums',
                i < step
                  ? 'border-accent bg-accent text-accent-fg'
                  : 'border-border-strong text-text-3 group-aria-selected:border-accent group-aria-selected:text-accent',
              )}
            >
              {i < step ? <Check className="size-3" /> : i + 1}
            </span>
            <span className="hidden md:inline">{t(`help.tutorial.${id}.title`)}</span>
            <span className="sr-only md:hidden">{t(`help.tutorial.${id}.title`)}</span>
          </RadixTabs.Trigger>
        ))}
      </RadixTabs.List>

      {TUTORIAL_STEPS.map((id, i) => {
        const tryIt = shortcutOf(TRY_IT[id]);
        return (
          <RadixTabs.Content key={id} value={String(i)} className="min-w-0 outline-none">
            <div className="mx-auto flex max-w-[600px] flex-col gap-4 px-5 py-5 sm:px-8">
              <div className="mx-auto w-full max-w-[440px] overflow-hidden rounded-xl border border-border-subtle bg-bg-1 p-2.5">
                <TutorialIllustration step={id} />
              </div>
              <div>
                <p className="text-xs text-text-3 tabular-nums">
                  {t('help.tutorial.stepOf', { n: i + 1, total })}
                </p>
                <h3 className="mt-1 text-lg font-semibold tracking-tight">
                  {t(`help.tutorial.${id}.title`)}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-text-2">
                  {t(`help.tutorial.${id}.p1`)}
                </p>
                <p className="mt-2 text-sm leading-relaxed text-text-2">
                  {t(`help.tutorial.${id}.p2`)}
                </p>
              </div>
              {tryIt && (
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 rounded-lg border border-border-subtle bg-bg-1 px-3 py-2">
                  <span className="text-2xs font-semibold tracking-wider text-accent uppercase">
                    {t('help.tutorial.tryIt')}
                  </span>
                  <span className="text-xs text-text-2">{t(`shortcuts.items.${tryIt.id}`)}</span>
                  <ShortcutKeys def={tryIt} />
                </div>
              )}
              <nav className="flex items-center justify-between gap-2 border-t border-border-subtle pt-3">
                <Button
                  variant="ghost"
                  leadingIcon={<ArrowLeft />}
                  disabled={i === 0}
                  onClick={() => {
                    setStep(i - 1);
                  }}
                >
                  {t('help.tutorial.back')}
                </Button>
                {i < last ? (
                  <Button
                    variant="primary"
                    onClick={() => {
                      setStep(i + 1);
                    }}
                  >
                    {t('help.tutorial.next')}
                    <ArrowRight aria-hidden className="size-4" />
                  </Button>
                ) : canTour ? (
                  <Button variant="primary" leadingIcon={<Compass />} onClick={startTour}>
                    {t('help.welcome.tour')}
                  </Button>
                ) : (
                  <Button variant="primary" onClick={close}>
                    {t('help.tutorial.done')}
                  </Button>
                )}
              </nav>
            </div>
          </RadixTabs.Content>
        );
      })}
    </RadixTabs.Root>
  );
}

// ── Shortcuts ────────────────────────────────────────────────────────────────

function Shortcuts() {
  const { t } = useTranslation();
  const groups = ['general', 'navigation', 'editing', 'view'] as const;
  return (
    <div className="mx-auto max-w-[760px] px-5 py-6 sm:px-8">
      <p className="text-sm text-text-2">{t('help.shortcuts.intro')}</p>
      <div className="mt-5 grid gap-x-10 gap-y-6 sm:grid-cols-2">
        {groups.map((group) => (
          <section key={group} aria-labelledby={`help-keys-${group}`}>
            <h3 id={`help-keys-${group}`} className="mb-2 text-xs font-medium text-text-3">
              {t(`shortcuts.groups.${group}`)}
            </h3>
            <dl className="divide-y divide-border-subtle">
              {SHORTCUTS.filter((s) => s.group === group).map((s) => (
                <div key={s.id} className="flex items-center justify-between gap-3 py-1.5 text-sm">
                  <dt className="text-text-2">{t(`shortcuts.items.${s.id}`)}</dt>
                  <dd>
                    <ShortcutKeys def={s} />
                  </dd>
                </div>
              ))}
            </dl>
          </section>
        ))}
      </div>
    </div>
  );
}
